import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { fr } from 'date-fns/locale';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '../ui/popover';
import './date-fr.css';

/* Date en français partout : jj/mm/aaaa tapée ou choisie dans le calendrier · la valeur reste « aaaa-mm-jj » comme un champ date */
const versTexte = (iso) => (/^\d{4}-\d{2}-\d{2}/.test(iso || '') ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '');
const versIso = (t) => {
  const s = String(t || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (!m) return null;
  const iso = `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) || d.getDate() !== Number(m[1]) ? null : iso;
};
const masque = (t) => { const c = t.replace(/[^\d]/g, '').slice(0, 8); return [c.slice(0, 2), c.slice(2, 4), c.slice(4)].filter(Boolean).join('/'); };
const enDate = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`) : undefined);
const isoDe = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const DateFr = forwardRef(({ value, defaultValue, onChange, onBlur, min, max, name, disabled, className = '', style, placeholder = 'jj/mm/aaaa', 'data-testid': testId, 'aria-label': aria, ...rest }, ref) => {
  const controle = value !== undefined;
  const [interne, setInterne] = useState(defaultValue || '');
  const iso = controle ? value || '' : interne;
  const [txt, setTxt] = useState(versTexte(iso));
  const [ouvert, setOuvert] = useState(false);
  const champ = useRef(null);
  useEffect(() => { setTxt(versTexte(iso)); }, [iso]);
  const synth = (v) => ({ target: { value: v, name }, currentTarget: { value: v, name } });
  const permis = (v) => v && v >= '1900-01-01' && (!min || v >= String(min).slice(0, 10)) && (!max || v <= String(max).slice(0, 10));
  const emettre = (v) => { if (!controle) setInterne(v); onChange?.(synth(v)); };
  const brancher = (el) => {
    champ.current = el;
    if (el) { el.showPicker = () => setOuvert(true); }
    if (typeof ref === 'function') ref(el); else if (ref) ref.current = el;
  };
  const taper = (e) => {
    const brut = e.target.value;
    const t = /^\d{4}-/.test(brut) ? brut : masque(brut);
    setTxt(t);
    const v = versIso(t);
    if (permis(v)) emettre(v); else if (!t) emettre('');
  };
  const choisir = (d) => { if (!d) return; const v = isoDe(d); setTxt(versTexte(v)); emettre(v); setOuvert(false); onBlur?.(synth(v)); };
  const sortir = () => { const v = permis(versIso(txt)) ? versIso(txt) : null; setTxt(versTexte(v || (txt ? iso : ''))); onBlur?.(synth(txt ? v || iso : '')); };
  return <Popover open={ouvert} onOpenChange={setOuvert}>
    <PopoverAnchor asChild><span className="dfr" style={style}>
      <input {...rest} ref={brancher} type="text" inputMode="numeric" className={`dfr-in ${className}`} value={txt} placeholder={placeholder} onChange={taper} onBlur={sortir} onClick={(e) => { rest.onClick?.(e); if (!e.isTrusted) setOuvert(true); }} disabled={disabled} name={name} aria-label={aria || 'date au format jj/mm/aaaa'} data-testid={testId} />
      <PopoverTrigger asChild><button type="button" className="dfr-btn" disabled={disabled} tabIndex={rest.tabIndex} aria-label="Ouvrir le calendrier" data-testid={testId ? `${testId}-calendrier` : undefined}><CalendarDays size={16} /></button></PopoverTrigger>
    </span></PopoverAnchor>
    <PopoverContent className="dfr-pop" align="start" collisionPadding={12} onOpenAutoFocus={(e) => e.preventDefault()} data-testid={testId ? `${testId}-popover` : undefined}>
      <Calendar mode="single" locale={fr} weekStartsOn={1} selected={enDate(iso)} defaultMonth={enDate(iso) || enDate(max) || new Date()} onSelect={choisir} disabled={[...(min ? [{ before: enDate(min) }] : []), ...(max ? [{ after: enDate(max) }] : [])]} />
    </PopoverContent>
  </Popover>;
});
DateFr.displayName = 'DateFr';
